import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAssetPath } from '../utils/assetHelper';
import { useUser } from '../context/UserContext';
import './WalletFinancePage.scss';

// Add Bootstrap type definition
declare global {
  interface Window {
    bootstrap: any;
  }
}

const WalletFinancePage = () => {
  const { userProfile, isLoading } = useUser();
  const [activeTab, setActiveTab] = useState('swap');
  const [solAmount, setSolAmount] = useState('');
  const [phanAmount, setPhanAmount] = useState('0');
  const [usdAmount, setUsdAmount] = useState('');
  const [showSwapSuccess, setShowSwapSuccess] = useState(false);
  const [showBuySuccess, setShowBuySuccess] = useState(false);
  const [showCouponSuccess, setShowCouponSuccess] = useState(false);
  
  // Phantom wallet states
  const [isPhantomInstalled, setIsPhantomInstalled] = useState(false);
  const [isWalletConnected, setIsWalletConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [solBalance, setSolBalance] = useState(0);
  
  // Set initial PHAN amount when userProfile loads
  useEffect(() => {
    if (userProfile && userProfile.valorPoints) {
      setPhanAmount(userProfile.valorPoints.toString());
    }
  }, [userProfile]);

  // Check if Phantom wallet is installed
  useEffect(() => {
    const checkPhantomWallet = async () => {
      const isPhantomAvailable = window.solana && window.solana.isPhantom;
      setIsPhantomInstalled(!!isPhantomAvailable);
      
      // Auto-connect if already connected
      if (isPhantomAvailable && window.solana && window.solana.isConnected) {
        setIsWalletConnected(true);
        setWalletAddress(window.solana.publicKey?.toString() || '');
        await fetchSolBalance();
      }
    };
    
    checkPhantomWallet();
  }, []);

  // Initialize any needed scripts or plugins for this page
  useEffect(() => {
    document.title = 'Wallet Finance - Phantom Clash';
    
    // Initialize tooltips
    if (typeof window !== 'undefined' && window.bootstrap) {
      const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
      tooltipTriggerList.map(function (tooltipTriggerEl) {
        return new window.bootstrap.Tooltip(tooltipTriggerEl);
      });
    }
  }, []);

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
  };

  const handleSolAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSolAmount(value);
    // Calculate PHAN amount (1 SOL = 4860 PHAN)
    if (value) {
      const phanValue = parseFloat(value) * 4860;
      setPhanAmount(phanValue.toString());
    } else {
      setPhanAmount(userProfile?.valorPoints?.toString() || '0');
    }
  };

  const handleUsdAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUsdAmount(value);
    // Calculate PHAN amount (1 USD = 100 PHAN for example)
    if (value) {
      const phanValue = parseFloat(value) * 100;
      setPhanAmount(phanValue.toString());
    } else {
      setPhanAmount(userProfile?.valorPoints?.toString() || '0');
    }
  };

  // Phantom wallet connection handlers
  const connectPhantomWallet = async () => {
    try {
      if (!isPhantomInstalled) {
        window.open('https://phantom.app/', '_blank');
        return;
      }
      
      const response = await window.solana?.connect();
      if (response) {
        setIsWalletConnected(true);
        setWalletAddress(response.publicKey.toString());
        await fetchSolBalance();
      }
    } catch (error) {
      console.error('Error connecting to Phantom wallet:', error);
    }
  };
  
  const disconnectPhantomWallet = async () => {
    try {
      if (window.solana) {
        await window.solana.disconnect();
        setIsWalletConnected(false);
        setWalletAddress('');
        setSolBalance(0);
      }
    } catch (error) {
      console.error('Error disconnecting Phantom wallet:', error);
    }
  };
  
  const fetchSolBalance = async () => {
    try {
      if (window.solana && window.solana.publicKey) {
        // Request SOL balance using Phantom's API
        const response = await window.solana.request({
          method: 'getBalance',
          params: { commitment: 'confirmed' }
        });
        
        if (response) {
          // SOL has 9 decimals, so divide by 10^9
          const solAmount = response.value / 1000000000;
          setSolBalance(solAmount);
        }
      }
    } catch (error) {
      console.error('Error fetching SOL balance:', error);
    }
  };

  const handleConfirmSwap = () => {
    setShowSwapSuccess(true);
  };

  const handleConfirmBuy = () => {
    setShowBuySuccess(true);
  };

  const handleConfirmCoupon = () => {
    setShowCouponSuccess(true);
  };

  // Format address for display: "abcd...wxyz"
  const formatAddress = (address: string) => {
    if (!address) return '';
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  // Show loading state while user data is being fetched
  if (isLoading) {
    return <div className="loading-indicator">Loading wallet data...</div>;
  }

  return (
    <>
      {/* Page Menu */}
      <div className="c-page-menu">
        <ul>
          <li className="active"><Link to="/wallet-finance">Finance</Link></li>
          <li><Link to="/wallet-transactions">Transactions</Link></li>
          <li><Link to="/wallet-security">Security Settings <span></span></Link></li>
        </ul>
      </div>

      <div className="d-block w-75">
        {/* Section Title */}
        <div className="c-section-title mt-0">
          <div className="d-flex flex-column justify-content-center">
            <h1>
              <img src={getAssetPath("assets/images/wallet-overview.svg")} alt="" />
              Phantom Wallet Overview
            </h1>
            <p className="c-section-title__desc">Manage your <span>$PHAN</span> tokens securely and access your wallet features</p>
          </div>
          <div className="p-wallet-overview__actions box ms-lg-auto ms-0 align-self-lg-end">
            {!isWalletConnected ? (
              <a 
                href="#" 
                className="c-i-button me-1" 
                onClick={(e) => { e.preventDefault(); connectPhantomWallet(); }}
                data-bs-toggle="tooltip" 
                data-bs-placement="top" 
                data-bs-title={isPhantomInstalled ? "Connect Phantom Wallet" : "Install Phantom Wallet"}
              >
                <i className="bi bi-plus-lg"></i>
              </a>
            ) : (
              <a 
                href="#" 
                className="c-i-button me-1"
                data-bs-toggle="tooltip" 
                data-bs-placement="top" 
                data-bs-title={`Connected: ${formatAddress(walletAddress)}`}
              >
                <i className="bi bi-check-circle-fill"></i>
              </a>
            )}
            <a href="#" className="c-i-button" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-title="Switch Wallet">
              <i className="bi bi-arrow-left-right"></i>
            </a>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="c-stats-card__container box">
          <div className="c-stats-card">
            <div className="c-stats-card__icon"><img src={getAssetPath("assets/images/phan-icon@1x.png")} alt="" /></div>
            <div className="c-stats-card__text">
              <h6>Wallet Balance</h6>
              <div className="c-stats-card__text-count">{userProfile?.valorPoints || 0}<small>.00</small></div>
              <p>$PHAN</p>
            </div>
          </div>
          <div className="c-stats-card light-blue">
            {isWalletConnected && (
              <a 
                href="#" 
                className="c-stats-card__hover red" 
                onClick={(e) => { e.preventDefault(); disconnectPhantomWallet(); }}
                data-bs-toggle="tooltip" 
                data-bs-placement="top" 
                data-bs-title="Disconnect Wallet"
              >
                <i className="bi bi-power"></i>
              </a>
            )}
            <div className="c-stats-card__icon"><img src={getAssetPath("assets/images/sol-icon.svg")} alt="" /></div>
            <div className="c-stats-card__text">
              <h6>SOL Balance</h6>
              {isWalletConnected ? (
                <div className="c-stats-card__text-count">{solBalance.toFixed(2)}<small> SOL</small></div>
              ) : (
                <div className="c-stats-card__text-connect">
                  <button className="c-button c-button--small" onClick={connectPhantomWallet}>
                    {isPhantomInstalled ? <><i className="bi bi-wallet-fill"></i> Connect Wallet</> : <><i className="bi bi-download"></i> Install Phantom</>}
                  </button>
                </div>
              )}
              <p>{isWalletConnected ? formatAddress(walletAddress) : 'Not connected'}</p>
            </div>
          </div>
        </div>

        {/* In-Page Menu */}
        <div className="c-in-page-menu box">
          <ul>
            <li className={activeTab === 'swap' ? 'active' : ''}>
              <a href="#swap" onClick={() => handleTabClick('swap')}><i className="bi bi-lightning-charge"></i> Swap</a>
            </li>
            <li className={activeTab === 'buy' ? 'active' : ''}>
              <a href="#buy" onClick={() => handleTabClick('buy')}><i className="bi bi-credit-card-2-back"></i> Buy</a>
            </li>
            <li className={activeTab === 'coupon' ? 'active' : ''}>
              <a href="#coupon" onClick={() => handleTabClick('coupon')}><i className="bi bi-sticky"></i> Coupon</a>
            </li>
          </ul>
        </div>

        {/* Swap Tab */}
        <div id="swap" className={`c-in-page-menu__content ${activeTab === 'swap' ? 'active' : ''}`}>
          <div className="box position-relative z-1 overflow-hidden">
            <div className="c-content-box">
              <div className="c-content-box__title">
                <h1>Swap <img src={getAssetPath("assets/images/sol-2-icon.svg")} alt="" /> SOL for $PHAN</h1>
                <div className="c-content-box__title-info">
                  <div className="c-content-box__title-price">
                    <h5>
                      <span><img src={getAssetPath("assets/images/sol-blue-icon.svg")} alt="" /> 1 SOL =</span>
                      <span>4860 $PHAN</span>
                    </h5>
                    <small>Updated 16.10.2024 18:28 GMT+4</small>
                  </div>
                </div>
              </div>
              {!showSwapSuccess ? (
                <div className="c-content-box__content" id="confirm-swap">
                  <h5 className="c-content-mini-title">Exchange your SOL tokens for $PHAN easily</h5>
                  {!isWalletConnected && (
                    <div className="c-content-box__connect-wallet mb-3">
                      <p><i className="bi bi-exclamation-circle"></i> Connect your Phantom wallet to swap SOL for $PHAN tokens</p>
                      <button className="c-button c-button--small" onClick={connectPhantomWallet}>
                        {isPhantomInstalled ? <><i className="bi bi-wallet-fill"></i> Connect Wallet</> : <><i className="bi bi-download"></i> Install Phantom Wallet</>}
                      </button>
                    </div>
                  )}
                  <div className="row position-relative">
                    <div className="col-12 mb-2">
                      <div className="c-form-row">
                        <div className="c-input-group mb-md-0 mb-2">
                          <div className="c-input-floating">
                            <input 
                              type="text" 
                              className="c-form-control" 
                              id="sol-amount" 
                              placeholder="Amount of SOL to swap"
                              value={solAmount}
                              onChange={handleSolAmountChange}
                            />
                            <i className="sol"></i>
                          </div>
                          <div className="c-form-error"></div>
                        </div>
                        <div className="c-form-selector d-md-flex d-none">=</div>
                        <div className="c-input-group mb-0">
                          <div className="c-input-floating">
                            <input 
                              type="text" 
                              className="c-form-control" 
                              value={phanAmount} 
                              disabled
                              aria-label="Resulting PHAN amount"
                              title="Resulting PHAN amount"
                            />
                            <i className="phan"></i>
                            <span className="symbol">$PHAN</span>
                          </div>
                          <div className="c-form-error"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12 mt-md-4 mt-2 d-flex flex-md-row flex-column align-items-center">
                      <button 
                        className="c-button" 
                        onClick={handleConfirmSwap}
                        disabled={!isWalletConnected || !solAmount}
                      >
                        Confirm Swap
                      </button>
                      <p className="c-info-text ms-md-4 mt-md-0 mt-3"><i className="bi bi-info-circle-fill"></i> A transaction fee of <b>0.0025 SOL</b> applies</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="c-content-box__content" id="confirm-swap-successful">
                  <div className="c-content-box__success">
                    <div className="c-content-box__success-icon">
                      <img src={getAssetPath("assets/images/magic-wand.gif")} alt="" />
                    </div>
                    <h1>Token Swap Successful!</h1>
                    <p>You've successfully swapped <b>{solAmount || '0'} SOL</b> for <b>{phanAmount} $PHAN</b>.<br /> Your new balance has been updated</p>
                    <h4>Tokens Received</h4>
                    <h2>{phanAmount} $phan</h2>
                    <div className="row">
                      <div className="col-12 d-flex align-content-center mt-3 mb-3">
                        <div className="c-content-box__success-item">
                          <small>Amount Swapped</small>
                          <span>{solAmount || '0'} SOL</span>
                        </div>
                        <div className="c-content-box__success-item">
                          <small>Fee Applied</small>
                          <span>0.0025 SOL</span>
                        </div>
                      </div>
                    </div>
                    <a href="#" className="c-button c-button--bordered c-button--xl mb-3">View Transaction History</a>
                    <div className="c-timer">
                      <div className="c-timer-bar"></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="c-content-box__bg">
              <video src={getAssetPath("assets/images/store-banner.mp4")} autoPlay loop muted playsInline></video>
            </div>
          </div>
        </div>

        {/* Buy Tab */}
        <div id="buy" className={`c-in-page-menu__content ${activeTab === 'buy' ? 'active' : ''}`}>
          <div className="box position-relative z-1 overflow-hidden">
            <div className="c-content-box">
              <div className="c-content-box__title">
                <h1>Purchase $PHAN with Card</h1>
                <div className="c-content-box__title-info">
                  <div className="c-content-box__title-price">
                    <h5>
                      <span><img src={getAssetPath("assets/images/sol-blue-icon.svg")} alt="" /> 1 SOL =</span>
                      <span>4860 $PHAN</span>
                    </h5>
                    <small>Updated 16.10.2024 18:28 GMT+4</small>
                    <video src={getAssetPath("assets/images/flash.mp4")} autoPlay loop muted playsInline></video>
                  </div>
                </div>
              </div>
              {!showBuySuccess ? (
                <div className="c-content-box__content" id="confirm-buy">
                  <h5 className="c-content-mini-title">Purchase $PHAN tokens instantly with your credit card</h5>
                  <div className="row position-relative">
                    <div className="col-12 mb-4">
                      <div className="c-form-row">
                        <div className="c-input-group mb-0">
                          <div className="c-input-floating">
                            <input 
                              type="text" 
                              className="c-form-control" 
                              id="usd-amount" 
                              placeholder="Enter amount to purchase"
                              value={usdAmount}
                              onChange={handleUsdAmountChange}
                            />
                            <i className="bi bi-currency-dollar"></i>
                          </div>
                          <div className="c-form-error"></div>
                        </div>
                        <div className="c-form-selector box-2 mt-md-0 mt-2 mb-md-0 mb-3">
                          <a href="#" className="c-i-button c-i-button--s" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-title="Switch Wallet">
                            <i className="bi bi-arrow-left-right"></i>
                          </a>
                        </div>
                        <div className="c-input-group mb-0">
                          <div className="c-input-floating">
                            <input 
                              type="text" 
                              className="c-form-control" 
                              id="phan-amount" 
                              value={phanAmount}
                              readOnly
                              aria-label="Resulting PHAN amount"
                              title="Resulting PHAN amount"
                            />
                            <i className="phan"></i>
                            <span className="symbol">$PHAN</span>
                          </div>
                          <div className="c-form-error"></div>
                        </div>
                      </div>
                    </div>
                    <div className="col-12 mb-2">
                      <h5 className="c-content-mini-title mb-md-3 mb-2"><i className="bi bi-credit-card-fill"></i> Card data</h5>
                    </div>
                    <div className="col-md-6 mb-md-0 mb-3">
                      <div className="c-input-group mb-0">
                        <div className="c-input-floating">
                          <input type="text" className="c-form-control" id="card-number" placeholder="Card Number" />
                          <img src={getAssetPath("assets/images/master-card.svg")} alt="" className="card-icon" />
                        </div>
                        <div className="c-form-error"></div>
                      </div>
                    </div>
                    <div className="col-md-3 col-6 mb-md-0 mb-3">
                      <div className="c-input-group mb-0">
                        <div className="c-input-floating">
                          <input type="text" className="c-form-control" id="card-expiry" placeholder="MM/YY" />
                        </div>
                        <div className="c-form-error"></div>
                      </div>
                    </div>
                    <div className="col-md-3 col-6 mb-md-0 mb-3">
                      <div className="c-input-group mb-0">
                        <div className="c-input-floating">
                          <input type="text" className="c-form-control" id="card-cvv" placeholder="CVV" />
                        </div>
                        <div className="c-form-error"></div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12 mt-md-4 mt-2 d-flex flex-md-row flex-column align-items-center">
                      <button className="c-button" onClick={handleConfirmBuy}>Confirm Purchase</button>
                      <p className="c-info-text ms-md-4 mt-md-0 mt-3"><i className="bi bi-info-circle-fill"></i> A processing fee of <b>2.5%</b> applies</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="c-content-box__content" id="confirm-buy-successful">
                  <div className="c-content-box__success">
                    <div className="c-content-box__success-icon">
                      <img src={getAssetPath("assets/images/party.gif")} alt="" />
                    </div>
                    <h1>Purchase Successful!</h1>
                    <p>You've successfully purchased <b>{phanAmount} $PHAN</b> tokens.<br /> Your wallet has been updated</p>
                    <h4>Tokens Received</h4>
                    <h2>{phanAmount} $phan</h2>
                    <div className="row">
                      <div className="col-12 d-flex align-content-center mt-3 mb-3">
                        <div className="c-content-box__success-item">
                          <small>Amount Paid</small>
                          <span>${usdAmount || '0'} USD</span>
                        </div>
                        <div className="c-content-box__success-item">
                          <small>Fee Applied</small>
                          <span>${(parseFloat(usdAmount || '0') * 0.025).toFixed(2)} USD</span>
                        </div>
                      </div>
                    </div>
                    <a href="#" className="c-button c-button--bordered c-button--xl mb-3">View Transaction History</a>
                    <div className="c-timer">
                      <div className="c-timer-bar"></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="c-content-box__bg">
              <video src={getAssetPath("assets/images/store-banner.mp4")} autoPlay loop muted playsInline></video>
            </div>
          </div>
        </div>

        {/* Coupon Tab */}
        <div id="coupon" className={`c-in-page-menu__content ${activeTab === 'coupon' ? 'active' : ''}`}>
          <div className="box position-relative z-1 overflow-hidden">
            <div className="c-content-box">
              <div className="c-content-box__title">
                <h1>Redeem Coupon Code</h1>
                <div className="c-content-box__title-info">
                  <div className="c-content-box__title-price">
                    <h5>
                      <span>Get $PHAN tokens with coupon codes</span>
                    </h5>
                  </div>
                </div>
              </div>
              {!showCouponSuccess ? (
                <div className="c-content-box__content" id="redeem-coupon">
                  <h5 className="c-content-mini-title">Enter your coupon code to receive $PHAN tokens</h5>
                  <div className="row position-relative">
                    <div className="col-12 mb-4">
                      <div className="c-input-group mb-0">
                        <div className="c-input-floating">
                          <input type="text" className="c-form-control" id="coupon-code" placeholder="Enter coupon code" />
                          <i className="bi bi-ticket-perforated"></i>
                        </div>
                        <div className="c-form-error"></div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12 mt-md-4 mt-2 d-flex flex-md-row flex-column align-items-center">
                      <button className="c-button" id="redeem-coupon-button" onClick={handleConfirmCoupon}>Redeem Coupon</button>
                      <p className="c-info-text ms-md-4 mt-md-0 mt-3"><i className="bi bi-info-circle-fill"></i> Coupon codes are case-sensitive</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="c-content-box__content" id="redeem-coupon-successful">
                  <div className="c-content-box__success">
                    <div className="c-content-box__success-icon">
                      <img src={getAssetPath("assets/images/magic-wand-2.gif")} alt="" />
                    </div>
                    <h1>Coupon Redeemed!</h1>
                    <p>You've successfully redeemed the coupon code <b>PHANTOM2024</b>.<br /> Your wallet has been credited with tokens</p>
                    <h4>Tokens Received</h4>
                    <h2>500 $phan</h2>
                    <a href="#" className="c-button c-button--bordered c-button--xl mb-3">View Transaction History</a>
                    <div className="c-timer">
                      <div className="c-timer-bar"></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="c-content-box__bg">
              <video src={getAssetPath("assets/images/store-banner.mp4")} autoPlay loop muted playsInline></video>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WalletFinancePage; 